import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { AccountBalance, ContentCopy } from "@mui/icons-material";
import DialogHeader from "./DialogHeader";
import { venueHirePaymentDeadlineNotice } from "./venueHireUtils";
import { MULTI_INVOICE_PAYMENT_TIP } from "./invoicePaymentUtils";

export interface BankTransferPaymentDetails {
  paymentReference: string;
  amount: number;
}

interface BankTransferPaymentDialogProps {
  open: boolean;
  details: BankTransferPaymentDetails | null;
  onClose: () => void;
  /** When true, shows the venue hire 12-hour payment deadline notice. */
  showPaymentDeadlineNotice?: boolean;
}

const ACCOUNT_NAME = "Easy Agility";
const SORT_CODE = "04-00-03";
const ACCOUNT_NUMBER = "66835282";

const BankTransferPaymentDialog: React.FC<BankTransferPaymentDialogProps> = ({
  open,
  details,
  onClose,
  showPaymentDeadlineNotice = false,
}) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const isMultiInvoiceReference =
    details?.paymentReference.includes(",") ?? false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title="Bank Transfer Payment"
        icon={<AccountBalance color="primary" />}
        onClose={onClose}
      />
      <DialogContent dividers>
        {details && (
          <Box>
            {showPaymentDeadlineNotice && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {venueHirePaymentDeadlineNotice}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 3 }}>
              Please make a bank transfer using the details below. Include the
              payment reference so we can match your payment.
            </Alert>
            {isMultiInvoiceReference && (
              <Alert severity="info" sx={{ mb: 2 }}>
                One bank transfer covers all invoices listed in the reference.
                Pay the combined total shown below.
              </Alert>
            )}
            <Paper sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
              <Grid container spacing={2}>
                {[
                  { label: "Account Name", value: ACCOUNT_NAME },
                  { label: "Sort Code", value: SORT_CODE },
                  { label: "Account Number", value: ACCOUNT_NUMBER },
                ].map(({ label, value }) => (
                  <Grid item xs={12} key={label}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {value}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(value)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Payment Reference
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "error.main" }}
                      >
                        {details.paymentReference}
                      </Typography>
                      <Typography variant="caption" color="error">
                        Important: Please include this reference
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        Multiple invoices: use INV-12,34 and pay the combined
                        total.
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(details.paymentReference)
                      }
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Amount to Pay
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "primary.main" }}
                  >
                    £{details.amount.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              {MULTI_INVOICE_PAYMENT_TIP}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankTransferPaymentDialog;
